import { useTranslation } from 'react-i18next'

import { IAddColumnBody } from '@makyoapp/makyo-sdk/api/Tables'
import { ColumnSettings, ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  ColumnAdapter,
  ColumnConnection,
  ColumnLookup,
  DEFAULT_COL_OPTIONS,
  DEFAULT_CONN_COL_OPTIONS_BACKEND,
  TableAdapter,
} from '@makyoapp/makyo-common'
import { Modal, ModalGridFooter } from '@makyoapp/makyo-ui'

import { formatColumnSettings } from '@/core/models/columns/helpers/get-column-settings-value'
import { useCreateColumnContext } from '@/context/CreateColumnContext'
import { useReqError } from '@/hooks/useReqError'
import { colTypeToLabel, generateColumnNameV2, generateLookupColumnName } from '@/shared/ag-grid'
import { MULTI_STEP_COL_TYPES } from '@/shared/ag-grid-constants'

import { useGridV2Context } from '../../contexts/GridV2Context'
import { ColumnSettingFields } from './ColumnSettingFields'
import { ColumnSettingFieldsStepTwo } from './ColumnSettingFieldsStepTwo'

interface ColumnFormProps {
  onSuccess?: (col?: ColumnAdapter) => void
  onHide?: () => void
  submitLabel?: string
  portalType?: boolean
  wrapClassName?: string
  onCancel?: () => void
}

export interface IColumnMetadata {
  tblId: string
  id: string
  colOptions: ColumnSettings
  connections: ColumnConnection
  lookup: ColumnLookup
}

export function ColumnForm({ onCancel, onHide, onSuccess, portalType, submitLabel, wrapClassName }: ColumnFormProps) {
  const { t } = useTranslation()
  const printAPIError = useReqError()

  const { value, step, setStep, error, buttonDisabled, isLoading, setIsLoading, reset, lookupCols } =
    useCreateColumnContext()

  const {
    table,
    tableList,
    handleAddColumn,
    columnToInsert,
    handleInsertColumn,
    handleCloseMenu,
    modalGridHeaderMenu,
    handleUpdateColumnSettings,
  } = useGridV2Context()

  const tblId = table?.id as string
  const isEdit = modalGridHeaderMenu?.type === 'edit'
  const isInsert = !!columnToInsert

  const columnToEdit = modalGridHeaderMenu?.type === 'edit' ? modalGridHeaderMenu.columnId : undefined

  let formSubmitLabel

  if (submitLabel || columnToEdit) {
    if (submitLabel) {
      formSubmitLabel = submitLabel
    } else {
      formSubmitLabel = t('Save')
    }
  } else {
    if (!MULTI_STEP_COL_TYPES.includes(value.type)) {
      formSubmitLabel = t('Create')
    } else {
      if (step < 2) {
        formSubmitLabel = t('Next')
      } else {
        formSubmitLabel = t('Create')
      }
    }
  }

  const generateLookupName = (tables: TableAdapter[], lookupColID?: string, lookupConnColId?: string) => {
    if (!lookupColID || !lookupConnColId) return ''
    const lookupConnection = lookupConnColId
    const lookupColumn = table?.columns.find((c) => c.id === lookupConnection)
    const lookupTableId = lookupColumn?.connections?.fTableId
    const lookupTable = tables.find((tb) => tb.id === lookupTableId)
    const selectedLookupColumnName = lookupTable?.columns.find((col) => col.id === lookupColID)?.name || ''

    if (selectedLookupColumnName.toLowerCase().includes('lookup')) {
      return selectedLookupColumnName
    }
    return `Lookup ${selectedLookupColumnName}`
  }

  const handleGenerateName = () => {
    if (!table) return

    let name = value.name

    if (!name) {
      if (value.type === ColumnTypes.Lookup) {
        name = generateLookupName(tableList, value.lookupOptions.lookupColumnId, value.lookupOptions.lookupConnColumnId)
      } else {
        name = `${colTypeToLabel(value.type).toString()}`
      }
    }

    return generateColumnNameV2(name, table, columnToEdit)
  }

  const formatNewLookupColumnPayload = (connectionColumnID: string, column: ColumnAdapter, idx: number) => {
    if (!table) return
    const name = generateLookupColumnName(`Lookup ${column.name}`, idx, table, columnToEdit)
    return {
      _id: '',
      _Name: name,
      _Type: ColumnTypes.Lookup,
      _Settings: DEFAULT_COL_OPTIONS as unknown as ColumnSettings,
      _Connection: DEFAULT_CONN_COL_OPTIONS_BACKEND,
      _Lookup: {
        _ForeignColumnIDToShow: column.id,
        _ConnectionColumnID: connectionColumnID,
        _Standard: false,
      },
    } as IAddColumnBody
  }

  const handleColumnSave = async () => {
    if (!table) return

    setIsLoading(true)

    // temporary ignoring types issue until we know what is the id on the IAddColumnBody should be
    const generatedName = handleGenerateName()

    const body = {
      _Name: generatedName,
      _Type: value.type,
      _Settings: formatColumnSettings(value),
      _Connection: {
        _LocalColumnID: value.connectionOptions.connColId,
        _ForeignTableID: value.connectionOptions.connFTableId,
        _ForeignColumnID: value.connectionOptions.connFColId,
        _ForeignColumnIDToShow: value.connectionOptions.connFColIdToShow,
        _MatchMultiple: value.connectionOptions.connMultiple,
      },
      _Lookup: {
        _ForeignColumnIDToShow: value.lookupOptions.lookupColumnId,
        _ConnectionColumnID: value.lookupOptions.lookupConnColumnId,
        _Standard: value.lookupOptions.standard,
      },
    } as IAddColumnBody

    let promise

    if (columnToEdit) {
      promise = handleUpdateColumnSettings(tblId, columnToEdit, body)
    } else if (columnToInsert) {
      promise = handleInsertColumn(table.id, columnToInsert.columnID, columnToInsert.positionToInsert, body)
    } else {
      promise = handleAddColumn(tblId, body).then(async (newColumn) => {
        // Manually add lookup columns if provided from new connection column settings
        if (newColumn && newColumn._Type === ColumnTypes.Connection && lookupCols.length > 0) {
          let connectionColumnID = newColumn._id

          await Promise.all(
            lookupCols.map((column, idx) => {
              let payload = formatNewLookupColumnPayload(connectionColumnID, column, idx + 1)
              if (!payload) return
              return handleAddColumn(tblId, payload)
            })
          )
        }
      })
    }

    promise
      .then((res) => {
        if (columnToEdit) {
          onSuccess?.()
          return null
        }
      })
      .catch(printAPIError)
      .finally(() => {
        onHide?.()
        reset()
        setIsLoading(false)
        if (handleCloseMenu) {
          handleCloseMenu()
        }
      })
  }

  return (
    <>
      <Modal.Body noPadding>
        {step === 1 && (
          <ColumnSettingFields
            type={value.type}
            isEdit={isEdit}
            isCustomSize={isEdit || isInsert}
            portalType={portalType}
            wrapClassName={wrapClassName}
          />
        )}
        {step === 2 && <ColumnSettingFieldsStepTwo wrapClassName={wrapClassName} />}
      </Modal.Body>

      <ModalGridFooter
        onHide={() => onCancel?.()}
        onCancel={() => onCancel?.()}
        onSubmit={() => {
          if (MULTI_STEP_COL_TYPES.includes(value.type) && step < 2 && !columnToEdit) {
            setStep(step + 1)
          } else if (tblId) {
            handleColumnSave()
          }
        }}
        disabled={Object.values(error).some((v) => v !== undefined && v !== '' && v !== null) || buttonDisabled}
        isLoading={isLoading}
        primaryLabel={formSubmitLabel}
        cancelLabel={t('Cancel')}
        onBack={() => setStep(step - 1)}
        showBack={step > 1}
        backLabel={t('Back')}
      />
    </>
  )
}
